import * as React from "react"
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md"
import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Image,
  SimpleGrid,
  Stack,
  Text,
  useColorModeValue,
} from "@chakra-ui/react"
import useEmblaCarousel from "embla-carousel-react"
import NextLink from "next/link"

const testimonialSlides = [
  {
    quote: "'Dat ik mensen blij kan maken met wat mij blij maakt, feels unreal! Dankzij Kunstbende!'",
    title: "Senn (15) - DJ",
    image: "Senn.jpeg",
  },
  {
    quote: "'Het leukste aan Kunstbende was het leren kennen van andere creative zielen'",
    title: "Sikandar  (19) - Filmer",
    image: "Sikandar.jpeg",
  },
  {
    quote: "'Door mee te doen aan Kunstbende heb ik meer zelfvertrouwen opgebouwd en goede vrienden ontmoet'",
    title: "Fenne (12) - Danser",
    image: "Fenne.jpg",
  },
]

export function JoinTheKlub() {
  const color = useColorModeValue("gray.700", "gray.400")
  const [currentSlide, setCurrentSlide] = React.useState(0)
  const displayCount = 1

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    slidesToScroll: displayCount,
    duration: 30,
  })

  const onSelect = React.useCallback(() => {
    if (!emblaApi) return
    setCurrentSlide(emblaApi.selectedScrollSnap())
  }, [emblaApi])

  React.useEffect(() => {
    if (!emblaApi) return
    onSelect()
    emblaApi.on("select", onSelect)
    emblaApi.on("reInit", onSelect)

    return () => {
      emblaApi.off("select", onSelect)
      emblaApi.off("reInit", onSelect)
    }
  }, [emblaApi, onSelect])

  const scrollPrev = React.useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev()
  }, [emblaApi])

  const scrollNext = React.useCallback(() => {
    if (emblaApi) emblaApi.scrollNext()
  }, [emblaApi])

  const scrollTo = React.useCallback(
    (index: number) => {
      if (emblaApi) emblaApi.scrollTo(index)
    },
    [emblaApi],
  )

  const canScrollPrev = currentSlide > 0
  const canScrollNext = currentSlide < testimonialSlides.length - 1

  return (
    <SimpleGrid 
      columns={{ base: 1, lg: 2 }} 
      spacing={{ base: 20, md: 10 }} 
      justifyContent="center"
      role="region"
      aria-label="Join the Klub section"
    >
      <Stack spacing={10} justifyContent="center">
        <Text 
          fontSize={{ base: 32, md: 48 }} 
          fontWeight={900} 
          lineHeight="shorter" 
          as="h2"
          id="join-klub-heading"
        >
          Join the{" "}
          <Text as="span" color="yellow.500">
            Klub
          </Text>
        </Text>
        <Text 
          color={color} 
          fontSize={{ base: "lg", md: "2xl" }} 
          lineHeight="short"
          aria-labelledby="join-klub-heading"
        >
          Kunstbende is de plek waar je jezelf laat zien, jezelf ontdekt, andere jonge makers ontmoet, van
          elkaar leert, samenwerkt, valt en weer opstaat, dealt met kritiek en groeit!
        </Text>
        <Button
          as={NextLink}
          passHref
          href="/inschrijven"
          fontSize={{ base: 16, md: 22 }}
          px={{ base: 8, md: 8 }}
          py={{ base: 6, md: 8 }}
          colorScheme="yellow"
          w="fit-content"
          aria-label="DOE MEE"
        >
          DOE MEE
        </Button>
      </Stack>

      <Stack spacing={0} justify="center" pl={{ base: 0, md: 10 }} role="group" aria-label="Testimonials slider">
        <Box 
          className="embla" 
          ref={emblaRef} 
          py={2}
          overflow="hidden"
          role="group"
          aria-label="Testimonials"
        >
          <Box className="embla__container" display="flex">
            {testimonialSlides.map(({ quote, title, image }, index) => (
              <Stack
                key={index}
                spacing={8}
                className="embla__slide"
                flex="0 0 100%"
                minW={0}
                overflow="visible"
                alignItems="center"
                role="group"
                aria-label={`Testimonial ${index + 1} van ${testimonialSlides.length}`}
                px="15px"
              >
                <Image
                  alt={`${title} profielfoto`}
                  src={image}
                  width={200}
                  height={200}
                  objectFit="cover"
                  alignSelf="center"
                  borderRadius="50%"
                  boxShadow="0px 9.5px 40px rgba(0, 0, 0, 0.3)"
                />
                <Stack w="70%" pb={6}>
                  <Text 
                    color={color} 
                    fontSize={20} 
                    lineHeight="normal" 
                    textAlign="center"
                    as="blockquote"
                  >
                    {quote}
                  </Text>
                  <Text 
                    color={color} 
                    fontSize={20} 
                    lineHeight="normal" 
                    textAlign="center"
                    as="cite"
                  >
                    {title}
                  </Text>
                </Stack>
              </Stack>
            ))}
          </Box>
        </Box>
        {emblaApi && testimonialSlides.length > displayCount && (
          <Flex justify="center" role="group" aria-label="Slider controls">
            <IconButton
              colorScheme="gray"
              variant="ghost"
              aria-label="Vorige testimonial"
              icon={<MdKeyboardArrowLeft size={48} aria-hidden="true" />}
              onClick={(e) => {
                e.stopPropagation()
                scrollPrev()
              }}
              disabled={!canScrollPrev}
              width="3rem"
              mr={5}
            />
            <HStack alignItems="center" spacing={5} role="group" aria-label="Slide indicators">
              {Array.from({ length: testimonialSlides.length }).map((_, i) => {
                const isActive = currentSlide === i
                return (
                  <Button
                    key={i}
                    size="xs"
                    w="18px"
                    h="18px"
                    minH="18px"
                    minW="18px"
                    p={0}
                    onClick={() => scrollTo(i)}
                    borderRadius="50%"
                    bgColor={isActive ? "pink.500" : "gray.300"}
                    aria-label={`Ga naar testimonial ${i + 1}`}
                    aria-current={isActive ? "true" : "false"}
                  />
                )
              })}
            </HStack>
            <IconButton
              colorScheme="gray"
              variant="ghost"
              aria-label="Volgende testimonial"
              icon={<MdKeyboardArrowRight size={48} aria-hidden="true" />}
              onClick={(e) => {
                e.stopPropagation()
                scrollNext()
              }}
              disabled={!canScrollNext}
              width="3rem"
              ml={5}
            />
          </Flex>
        )}
      </Stack>
    </SimpleGrid>
  )
}
